import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Store, CheckCircle, ArrowRight, ArrowLeft, Loader2, 
  Key, Package, Code, Rocket, ExternalLink,
  AlertCircle, Info, Building2, FileText, Shield
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { type Currency } from "@/lib/currency";
import { finalizePendingShopifyInstall, getPendingShopifyContext, setPendingShopifyContext } from "@/lib/shopifyInstall";
import {
  clearShopifyAutoReconnectAttempt,
  maybeStartShopifyAutoReconnect,
} from "@/lib/shopifyAutoReconnect";
import { getPreferredMerchantAssociation, normalizeShopDomain } from "@/lib/merchantSelection";
import { buildEmbeddedAppPath, getShopifyEmbeddedContext, setShopifyEmbeddedContext } from "@/lib/shopifyEmbedded";
import { invokeShopifyEmbeddedFunction } from "@/lib/shopifySessionToken";
import {
  completePendingMerchantSetup,
  getPendingMerchantSetup,
  savePendingMerchantSetup,
  type PendingMerchantSetup,
} from "@/lib/merchantSetup";
import { GST_MODULE_ENABLED } from "@/lib/featureFlags";
import sampleProductImage from "@/assets/sample-product.jpg";

interface MerchantInfo {
  id: string;
  store_name: string;
  shopify_store_domain: string;
  shopify_access_token: string | null;
  platform_type?: string | null;
  webhook_secret?: string | null;
  business_email: string | null;
  business_phone: string | null;
  business_address: string | null;
  gst_number: string | null;
  pan_number: string | null;
  default_deadline_hours: number | null;
  default_tax_rate: number | null;
  onboarding_completed_at: string | null;
  currency: Currency | null;
}

// Product interface removed - split payments are now store-wide

interface BusinessDetails {
  business_email: string;
  business_phone: string;
  business_address: string;
  gst_number: string;
  pan_number: string;
  contact_person_name: string;
  contact_person_email: string;
  contact_person_phone: string;
  store_category: string;
  store_category_other: string;
  currency: Currency;
}

type OnboardingStep = "welcome" | "quick" | "business" | "permissions" | "widget" | "complete";

const STEPS: { key: OnboardingStep; title: string; icon: React.ReactNode }[] = [
  { key: "welcome", title: "Welcome", icon: <Store className="h-4 w-4" /> },
  { key: "business", title: "Business Info", icon: <Building2 className="h-4 w-4" /> },
  { key: "permissions", title: "Permissions", icon: <Shield className="h-4 w-4" /> },
  { key: "widget", title: "Widget", icon: <Code className="h-4 w-4" /> },
  { key: "complete", title: "Complete", icon: <Rocket className="h-4 w-4" /> },
];

const QUICK_STEPS: { key: OnboardingStep; title: string; icon: React.ReactNode }[] = [
  { key: "welcome", title: "Welcome", icon: <Store className="h-4 w-4" /> },
  { key: "quick", title: "Quick Setup", icon: <Rocket className="h-4 w-4" /> },
  { key: "widget", title: "Widget", icon: <Code className="h-4 w-4" /> },
  { key: "complete", title: "Complete", icon: <Rocket className="h-4 w-4" /> },
];

const normalizeOnboardingStep = (value: string | null): OnboardingStep => {
  switch (value) {
    case "business":
    case "permissions":
    case "widget":
    case "complete":
    case "quick":
      return value;
    case "connect":
      return "widget";
    default:
      return "welcome";
  }
};

const isValidWooCommerceStoreUrl = (value: string) => {
  try {
    const parsed = new URL(value.startsWith("http") ? value : `https://${value}`);
    return ["http:", "https:"].includes(parsed.protocol) && parsed.hostname.includes(".");
  } catch {
    return false;
  }
};

const normalizeWooCommerceStoreUrl = (value: string) => {
  const parsed = new URL(value.startsWith("http") ? value : `https://${value}`);
  const pathname = parsed.pathname.replace(/\/+$/, "");
  return `${parsed.origin}${pathname}`;
};

const MerchantOnboarding = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const getEmbeddedMerchantPath = useCallback((path: string) => buildEmbeddedAppPath(path), []);
  
  // Shopify app installs can skip the introductory screen because Shopify
  // already supplied the store identity and connection context.
  const isShopifyAppContext = Boolean(
    searchParams.get("shop") ||
    searchParams.get("host") ||
    searchParams.get("embedded") === "1",
  );
  const initialStep = searchParams.has("step")
    ? normalizeOnboardingStep(searchParams.get("step"))
    : isShopifyAppContext
      ? "quick"
      : "welcome";
  const [step, setStep] = useState<OnboardingStep>(initialStep);
  const [merchant, setMerchant] = useState<MerchantInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [noMerchantRecord, setNoMerchantRecord] = useState(false);
  const [pendingSetup, setPendingSetup] = useState<PendingMerchantSetup | null>(null);
  const [pendingStoreDomain, setPendingStoreDomain] = useState("");
  const [retryingSetup, setRetryingSetup] = useState(false);
  const [pendingSetupError, setPendingSetupError] = useState<string | null>(null);
  
  // Business details state
  const [businessDetails, setBusinessDetails] = useState<BusinessDetails>({
    business_email: "",
    business_phone: "",
    business_address: "",
    gst_number: "",
    pan_number: "",
    contact_person_name: "",
    contact_person_email: "",
    contact_person_phone: "",
    store_category: "",
    store_category_other: "",
    currency: "INR",
  });
  const [savingBusiness, setSavingBusiness] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [permissionsAccepted, setPermissionsAccepted] = useState(false);
  
  // Widget step state
  const [widgetPosition, setWidgetPosition] = useState<"image-top-left" | "next-to-title">("image-top-left");
  const [installingWidget, setInstallingWidget] = useState(false);
  const [widgetInstalled, setWidgetInstalled] = useState(false);
  const [requiresThemeExtension, setRequiresThemeExtension] = useState(false);
  
  // Ref to prevent multiple OAuth initiations
  const oauthInitiatedRef = useRef(false);

  // Handle Shopify app install redirect - auto-initiate OAuth
  useEffect(() => {
    // Prevent multiple executions
    if (oauthInitiatedRef.current) return;
    
    const shop = searchParams.get("shop");
    const host = searchParams.get("host");
    const hmac = searchParams.get("hmac");
    const oauthSuccess = searchParams.get("oauth_success");
    const oauthError = searchParams.get("oauth_error");

    // If coming from Shopify app install (has shop + hmac but not oauth callback)
    if (shop && hmac && !oauthSuccess && !oauthError) {
      console.log("Detected Shopify app install for:", shop);
      
      // Store shop domain for after login
      setPendingShopifyContext({ shop });
      setShopifyEmbeddedContext({
        embedded: searchParams.get("embedded") === "1" || Boolean(host),
        host,
        shop,
      });
      
      // If user is logged in and has merchant, check if already onboarded
      if (user && merchant) {
        // If merchant is already onboarded, redirect to dashboard
        if (merchant.onboarding_completed_at) {
          console.log("Merchant already onboarded, redirecting to dashboard");
          // Keep the app inside Shopify admin instead of breaking out to the standalone app URL.
          navigate(getEmbeddedMerchantPath("/merchant-dashboard"), { replace: true });
          return;
        }
        
        // Mark as initiated to prevent duplicate opens
        oauthInitiatedRef.current = true;
        
        // Use the edge function to initiate OAuth - it has the correct scopes and credentials
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const shopDomain = shop.includes('.myshopify.com') ? shop : `${shop}.myshopify.com`;
        
        const oauthInitUrl = new URL(`${supabaseUrl}/functions/v1/shopify-oauth-init`);
        oauthInitUrl.searchParams.set('shop', shopDomain);
        oauthInitUrl.searchParams.set('merchant_id', merchant.id);
        const embeddedHost = host || getShopifyEmbeddedContext(searchParams).host;
        if (embeddedHost) {
          oauthInitUrl.searchParams.set("host", embeddedHost);
        }
        
        // CRITICAL: Break out of Shopify's iframe completely to avoid cookie issues
        if (window.top !== window.self) {
          window.top!.location.href = oauthInitUrl.toString();
        } else {
          window.location.href = oauthInitUrl.toString();
        }
        return;
      }
    }
  }, [getEmbeddedMerchantPath, merchant, navigate, searchParams, user]);

  // Handle OAuth callback result
  useEffect(() => {
    const oauthSuccess = searchParams.get("oauth_success");
    const oauthError = searchParams.get("oauth_error");
    const shopName = searchParams.get("shop_name") || searchParams.get("shop");
    const currentStep = searchParams.get("step");
    const normalizedCurrentStep = normalizeOnboardingStep(currentStep);

    if (oauthSuccess === "true") {
      toast.success(`Connected to "${shopName || "your store"}"!`);
      const nextParams = new URLSearchParams();
      nextParams.set("step", "widget");
      const embeddedContext = getShopifyEmbeddedContext(searchParams);
      if (embeddedContext.host) {
        nextParams.set("host", embeddedContext.host);
        nextParams.set("embedded", "1");
      }
      if (embeddedContext.shop) {
        nextParams.set("shop", embeddedContext.shop);
      }
      setSearchParams(nextParams);
      setStep("widget");
    } else if (oauthError) {
      toast.error(`Connection failed: ${oauthError}`);
      const nextParams = new URLSearchParams();
      if (currentStep) {
        nextParams.set("step", normalizedCurrentStep);
      }
      const embeddedContext = getShopifyEmbeddedContext(searchParams);
      if (embeddedContext.host) {
        nextParams.set("host", embeddedContext.host);
        nextParams.set("embedded", "1");
      }
      if (embeddedContext.shop) {
        nextParams.set("shop", embeddedContext.shop);
      }
      setSearchParams(nextParams);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!merchant) {
      return;
    }

    const embeddedShop = getShopifyEmbeddedContext(searchParams).shop;

    if (merchant.shopify_access_token) {
      clearShopifyAutoReconnectAttempt(merchant.id, embeddedShop);
      return;
    }

    if (searchParams.get("oauth_success") || searchParams.get("oauth_error")) {
      return;
    }

    const startedReconnect = maybeStartShopifyAutoReconnect({
      hasAccessToken: merchant.shopify_access_token,
      merchantId: merchant.id,
      merchantShopDomain: merchant.shopify_store_domain,
      searchParams,
    });

    if (startedReconnect) {
      toast.info("Finishing your Shopify connection...");
    }
  }, [merchant, searchParams]);

  useEffect(() => {
    if (!authLoading && !user) {
      // Preserve current URL params when redirecting to login
      const currentUrl = window.location.pathname + window.location.search;
      navigate(getEmbeddedMerchantPath(`/merchant-login?redirect=${encodeURIComponent(currentUrl)}`));
    } else if (user) {
      fetchMerchantInfo();
    }
  }, [authLoading, getEmbeddedMerchantPath, navigate, user]);

  useEffect(() => {
    const setup = getPendingMerchantSetup(user);
    setPendingSetup(setup);
    setPendingStoreDomain(setup?.storeDomain || "");
  }, [user, noMerchantRecord]);

  const fetchMerchantInfo = async () => {
    try {
      const embeddedShop = normalizeShopDomain(searchParams.get("shop"));
      const { data: merchantUsers, error: merchantUsersError } = await supabase
        .from("merchant_users")
        .select("merchant_id, merchants!inner(shopify_store_domain)")
        .eq("user_id", user!.id);

      if (merchantUsersError) {
        throw merchantUsersError;
      }

      const merchantAssociation = getPreferredMerchantAssociation(merchantUsers as any[], {
        preferredShop: embeddedShop,
        storedMerchantId: localStorage.getItem("active_merchant_id"),
      });
      const merchantId = merchantAssociation?.merchant_id ?? null;
      
      if (!merchantId) {
        try {
          const repairedMerchantId = await completePendingMerchantSetup(user);
          if (repairedMerchantId) {
            localStorage.setItem("active_merchant_id", repairedMerchantId);
            toast.success("Store setup completed successfully.");

            const { data: repairedMerchant, error: repairedError } = await supabase
              .from("merchants")
              .select("*")
              .eq("id", repairedMerchantId)
              .single();

            if (repairedError || !repairedMerchant) {
              throw repairedError || new Error("Merchant could not be loaded after setup repair");
            }

            setMerchant({
              id: repairedMerchant.id,
              store_name: repairedMerchant.store_name,
              shopify_store_domain: repairedMerchant.shopify_store_domain,
              shopify_access_token: repairedMerchant.shopify_access_token,
              platform_type: repairedMerchant.platform_type,
              webhook_secret: repairedMerchant.webhook_secret,
              business_email: repairedMerchant.business_email,
              business_phone: repairedMerchant.business_phone,
              business_address: repairedMerchant.business_address,
              gst_number: repairedMerchant.gst_number,
              pan_number: repairedMerchant.pan_number,
              default_deadline_hours: repairedMerchant.default_deadline_hours,
              default_tax_rate: repairedMerchant.default_tax_rate,
              onboarding_completed_at: repairedMerchant.onboarding_completed_at,
              currency: repairedMerchant.currency as Currency | null,
            });
            setWidgetInstalled(Boolean((repairedMerchant as any).widget_installed));
            setNoMerchantRecord(false);
            setLoading(false);
            return;
          }
        } catch (repairError) {
          console.error("Failed to repair pending merchant setup:", repairError);
          toast.error("Could not finish your store setup automatically. Please sign out and try again.");
        }

        // No merchant record - check if we have a pending Shopify install to finalize
        const pendingContext = getPendingShopifyContext();
        
        if (pendingContext.shop && pendingContext.sessionToken) {
          console.log("Finalizing pending Shopify install for onboarding:", pendingContext.shop);

          try {
            const finalizedInstall = await finalizePendingShopifyInstall(pendingContext.sessionToken);

            if (!finalizedInstall?.merchantId) {
              throw new Error("Merchant finalization did not return a merchant ID");
            }

            const { data: newMerchant, error: createError } = await supabase
              .from("merchants")
              .select("*")
              .eq("id", finalizedInstall.merchantId)
              .single();

            if (createError || !newMerchant) {
              throw createError || new Error("Merchant could not be loaded after finalization");
            }

            toast.success("Shopify store connected! Let's complete your setup.");
            localStorage.setItem("active_merchant_id", newMerchant.id);

            setMerchant({
              id: newMerchant.id,
              store_name: newMerchant.store_name,
              shopify_store_domain: newMerchant.shopify_store_domain,
              shopify_access_token: newMerchant.shopify_access_token,
              platform_type: newMerchant.platform_type,
              webhook_secret: newMerchant.webhook_secret,
              business_email: newMerchant.business_email,
              business_phone: newMerchant.business_phone,
              business_address: newMerchant.business_address,
              gst_number: newMerchant.gst_number,
              pan_number: newMerchant.pan_number,
              default_deadline_hours: newMerchant.default_deadline_hours,
              default_tax_rate: newMerchant.default_tax_rate,
              onboarding_completed_at: newMerchant.onboarding_completed_at,
              currency: newMerchant.currency as Currency | null,
            });
            setWidgetInstalled(Boolean((newMerchant as any).widget_installed));
            setLoading(false);
            return;
          } catch (finalizeError) {
            console.error("Failed to finalize Shopify merchant:", finalizeError);
            toast.error("Failed to set up your store. Please try again.");
            setNoMerchantRecord(true);
            setLoading(false);
            return;
          }
        }
        
        // No pending data - show error state
        console.log("No merchant found for user and no pending Shopify data");
        setNoMerchantRecord(true);
        setLoading(false);
        return;
      }

      localStorage.setItem("active_merchant_id", merchantId);

      const { data: merchantData, error } = await supabase
        .from("merchants")
        .select("id, store_name, shopify_store_domain, shopify_access_token, platform_type, webhook_secret, business_email, business_phone, business_address, gst_number, pan_number, default_deadline_hours, default_tax_rate, onboarding_completed_at, widget_installed, currency")
        .eq("id", merchantId)
        .single();

      if (error || !merchantData) {
        console.error("Failed to load merchant:", error);
        toast.error("Failed to load merchant information");
        setNoMerchantRecord(true);
        setLoading(false);
        return;
      }

      // If merchant is already onboarded, redirect to dashboard immediately
      if (merchantData.onboarding_completed_at) {
        console.log("Merchant already onboarded, redirecting to dashboard");
        navigate(getEmbeddedMerchantPath("/merchant-dashboard"), { replace: true });
        return;
      }

      // Shopify app launches are already authenticated and connected. Do not
      // send merchants through the legacy onboarding wizard; open the
      // dashboard directly and show the storefront app-embed action there.
      if (merchantData.platform_type === "shopify" && isShopifyAppContext) {
        await supabase
          .from("merchants")
          .update({ onboarding_completed_at: new Date().toISOString() })
          .eq("id", merchantData.id);
        navigate(getEmbeddedMerchantPath("/merchant-dashboard"), { replace: true });
        return;
      }

      setMerchant(merchantData);
      
      const pendingContext = getPendingShopifyContext();
      const resolvedCurrency =
        merchantData.currency ||
        (pendingContext.shopCurrency as Currency | null) ||
        "INR";

      // Pre-fill business details if already saved
      setBusinessDetails({
        business_email: merchantData.business_email || user?.email || "",
        business_phone: merchantData.business_phone || "",
        business_address: merchantData.business_address || "",
        gst_number: merchantData.gst_number || "",
        pan_number: merchantData.pan_number || "",
        contact_person_name: (merchantData as any).contact_person_name || "",
        contact_person_email: (merchantData as any).contact_person_email || "",
        contact_person_phone: (merchantData as any).contact_person_phone || "",
        store_category: (merchantData as any).store_category || "",
        store_category_other: "",
        currency: resolvedCurrency,
      });
      
      if ((merchantData as any).widget_installed) {
        setWidgetInstalled(true);
        console.log("Store already connected and widget installed");
      }
    } catch (error) {
      console.error("Error fetching merchant:", error);
      toast.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBusinessDetails = async () => {
    if (!merchant) return;
    const isShopifyAppContext = Boolean(
      searchParams.get("shop") ||
      searchParams.get("host") ||
      searchParams.get("embedded") === "1",
    );
    const isShopifyQuickSetup = merchant.platform_type === "shopify" && isShopifyAppContext;

    if (!termsAccepted) {
      toast.error("Please accept the terms and conditions");
      return;
    }
    if (!isShopifyQuickSetup && (!businessDetails.business_email || !businessDetails.business_phone || !businessDetails.contact_person_name || !businessDetails.contact_person_email || !businessDetails.store_category)) {
      toast.error("Please fill in required fields");
      return;
    }
    if (!isShopifyQuickSetup && businessDetails.store_category === "other" && !businessDetails.store_category_other) {
      toast.error("Please specify your store category");
      return;
    }

    setSavingBusiness(true);
    try {
      if (isShopifyQuickSetup) {
        const contactEmail = businessDetails.business_email || user?.email || null;
        const contactName = businessDetails.contact_person_name || merchant.store_name;
        const { error } = await supabase
          .from("merchants")
          .update({
            business_email: contactEmail,
            contact_person_name: contactName,
            contact_person_email: contactEmail,
            currency: businessDetails.currency,
            terms_accepted_at: new Date().toISOString(),
          })
          .eq("id", merchant.id);

        if (error) throw error;

        toast.success("Quick setup complete. Let’s enable Splitkart on your storefront.");
        handleStepChange("widget");
        return;
      }

      const finalCategory = businessDetails.store_category === "other" 
        ? businessDetails.store_category_other 
        : businessDetails.store_category;

      const { error } = await supabase
        .from("merchants")
        .update({
          business_email: businessDetails.business_email,
          business_phone: businessDetails.business_phone,
          business_address: businessDetails.business_address,
          gst_number: businessDetails.gst_number || null,
          pan_number: businessDetails.pan_number || null,
          contact_person_name: businessDetails.contact_person_name,
          contact_person_email: businessDetails.contact_person_email,
          contact_person_phone: businessDetails.contact_person_phone || null,
          store_category: finalCategory,
          currency: businessDetails.currency,
          terms_accepted_at: new Date().toISOString(),
        } as any)
        .eq("id", merchant.id);

      if (error) throw error;

      toast.success("Business details saved!");
      handleStepChange("permissions");
    } catch (error) {
      console.error("Error saving business details:", error);
      toast.error("Failed to save details");
    } finally {
      setSavingBusiness(false);
    }
  };

  const handleInstallWidget = async () => {
    if (!merchant) return;

    if (merchant.platform_type === "woocommerce") {
      setInstallingWidget(true);
      try {
        const { error } = await supabase
          .from("merchants")
          .update({ widget_installed: true })
          .eq("id", merchant.id);

        if (error) throw error;

        setWidgetInstalled(true);
        toast.success("WooCommerce connector marked as installed.");
        handleStepChange("complete");
      } catch (error) {
        console.error("Error marking WooCommerce connector installed:", error);
        toast.error("Could not update connector status. You can continue and update it later.");
      } finally {
        setInstallingWidget(false);
      }
      return;
    }
    
    setInstallingWidget(true);
    try {
      const { data, error } = await invokeShopifyEmbeddedFunction("install-widget", {
        body: { 
          merchantId: merchant.id, 
          action: "install",
          position: widgetPosition,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.requiresThemeExtension) {
        setRequiresThemeExtension(true);
        toast.success(data.message || "Enable the Splitkart app embed in Shopify Theme Editor to finish storefront setup.");
        handleStepChange("complete");
        return;
      }

      setWidgetInstalled(true);
      toast.success("Widget installed successfully!");
      
      // Move to complete step after successful installation
      handleStepChange("complete");
    } catch (error: any) {
      console.error("Error installing widget:", error);
      // Check if it's a scope/permission error
      const errorMsg = error.message || "Failed to install widget";
      if (errorMsg.includes("scope") || errorMsg.includes("approval") || errorMsg.includes("access denied")) {
        toast.error("Widget installation failed - you may need to reconnect with updated permissions. Proceeding to complete setup.", {
          duration: 5000,
        });
      } else {
        toast.error(`${errorMsg}. You can install manually from your dashboard.`, {
          duration: 5000,
        });
      }
      // Navigate to complete step even on error so user can proceed
      handleStepChange("complete");
    } finally {
      setInstallingWidget(false);
    }
  };

  const handleStepChange = (newStep: OnboardingStep) => {
    setStep(newStep);
    // Persist step to URL for OAuth redirect preservation
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set("step", newStep);
      return newParams;
    });
  };

  const handleComplete = async () => {
    if (!merchant) return;

    // Start trial and mark onboarding complete (30 days trial period)
    const trialStart = new Date();
    const trialEnd = new Date();
    trialEnd.setDate(trialEnd.getDate() + 30);

    await supabase
      .from("merchants")
      .update({
        plan: "trial",
        subscription_status: "trialing",
        trial_started_at: trialStart.toISOString(),
        trial_ends_at: trialEnd.toISOString(),
        splits_this_month: 0,
        onboarding_completed_at: new Date().toISOString(),
      })
      .eq("id", merchant.id);

    navigate(getEmbeddedMerchantPath("/merchant-dashboard"));
  };

  const isShopifyQuickOnboarding = Boolean(
    merchant?.platform_type === "shopify" &&
    (searchParams.get("shop") || searchParams.get("host") || searchParams.get("embedded") === "1"),
  );
  const onboardingSteps = isShopifyQuickOnboarding ? QUICK_STEPS : STEPS;
  const currentStepIndex = onboardingSteps.findIndex(s => s.key === step);
  const progressPercent = ((currentStepIndex + 1) / onboardingSteps.length) * 100;
  const isWooCommerceMerchant = merchant?.platform_type === "woocommerce";
  const storeConnected = isWooCommerceMerchant || Boolean(merchant?.shopify_access_token);
  

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show error UI if no merchant record found (prevents infinite loop)
  if (noMerchantRecord || !merchant) {
    const handleSignOut = async () => {
      await supabase.auth.signOut();
      navigate(getEmbeddedMerchantPath("/merchant-login?tab=signup"));
    };

    const handleRetry = () => {
      setNoMerchantRecord(false);
      setLoading(true);
      window.location.reload();
    };

    const isPendingWooCommerceSetup = pendingSetup?.platformType === "woocommerce";

    const handleRetryPendingSetup = async () => {
      if (!user || !pendingSetup) {
        handleRetry();
        return;
      }

      const trimmedDomain = pendingStoreDomain.trim();
      if (!trimmedDomain) {
        setPendingSetupError("Enter your WooCommerce store URL to continue.");
        return;
      }

      if (!isValidWooCommerceStoreUrl(trimmedDomain)) {
        setPendingSetupError("Enter a valid WooCommerce store URL, including any WordPress subdirectory if you use one.");
        return;
      }

      const normalizedDomain = normalizeWooCommerceStoreUrl(trimmedDomain);
      const updatedSetup: PendingMerchantSetup = {
        ...pendingSetup,
        storeDomain: normalizedDomain,
      };

      setRetryingSetup(true);
      setPendingSetupError(null);
      savePendingMerchantSetup(updatedSetup);
      setPendingSetup(updatedSetup);
      setPendingStoreDomain(normalizedDomain);

      try {
        setNoMerchantRecord(false);
        setLoading(true);
        await fetchMerchantInfo();
      } catch (error) {
        console.error("Failed to retry merchant setup:", error);
        setPendingSetupError("We still couldn't link this store. Double-check the full production URL and try again.");
      } finally {
        setRetryingSetup(false);
      }
    };

    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Account Setup Issue</CardTitle>
            <CardDescription>
              We couldn't finish linking your store automatically. You can retry now, or update the WooCommerce store URL if it needs a different domain or subdirectory.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isPendingWooCommerceSetup && (
              <div className="space-y-2">
                <Label htmlFor="pending-store-domain">WooCommerce Store URL</Label>
                <Input
                  id="pending-store-domain"
                  value={pendingStoreDomain}
                  onChange={(event) => setPendingStoreDomain(event.target.value)}
                  placeholder="https://yourstore.com/shop"
                  autoComplete="url"
                />
                <p className="text-xs text-muted-foreground">
                  Use the live store URL exactly as customers load it. Keep the WordPress subdirectory if your site is installed under one.
                </p>
                {pendingSetupError ? (
                  <p className="text-sm text-destructive">{pendingSetupError}</p>
                ) : null}
                <Button onClick={handleRetryPendingSetup} className="w-full" disabled={retryingSetup}>
                  {retryingSetup ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Update Store URL & Retry
                </Button>
              </div>
            )}
            <Button onClick={handleRetry} className="w-full" variant={isPendingWooCommerceSetup ? "outline" : "default"}>
              Try Again
            </Button>
            <Button onClick={handleSignOut} className="w-full" variant="outline">
              Sign Out & Start Fresh
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Progress Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold">Setup Your Store</h1>
            <span className="text-sm text-muted-foreground">
              Step {currentStepIndex + 1} of {onboardingSteps.length}
            </span>
          </div>
          <Progress value={progressPercent} className="h-2" />
          <div className="flex justify-between mt-2">
            {onboardingSteps.map((s, index) => (
              <button
                key={s.key}
                onClick={() => {
                  if (index <= currentStepIndex) {
                    handleStepChange(s.key);
                  }
                }}
                className={`flex flex-col items-center gap-1 text-xs transition-colors ${
                  index <= currentStepIndex
                    ? "text-primary cursor-pointer"
                    : "text-muted-foreground cursor-not-allowed"
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  index < currentStepIndex
                    ? "bg-primary text-primary-foreground"
                    : index === currentStepIndex
                    ? "bg-primary/20 text-primary border-2 border-primary"
                    : "bg-muted"
                }`}>
                  {index < currentStepIndex ? <CheckCircle className="h-4 w-4" /> : s.icon}
                </div>
                <span className="hidden sm:block">{s.title}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Step Content */}
        {step === "welcome" && (
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Store className="h-8 w-8 text-primary" />
              </div>
              {/* TODO: Enable returning merchant detection after finalizing onboarding flow
              {(merchant.shopify_access_token || merchant.business_email) ? (
                <>
                  <CardTitle className="text-2xl">Welcome Back! 👋</CardTitle>
                  <CardDescription className="text-base">
                    Happy to see you again! Pick up where you left off and let's make group gifting simple for <strong>{merchant.store_name}</strong>
                  </CardDescription>
                </>
              ) : ( ... )}
              */}
              <CardTitle className="text-2xl">Welcome to Splitkart!</CardTitle>
              <CardDescription className="text-base">
                Let's set up split payments for <strong>{merchant.store_name}</strong>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4">
                <div className="flex items-start gap-4 p-4 rounded-lg border bg-card">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">Business Details</h3>
                    <p className="text-sm text-muted-foreground">
                      Tell us about your business for invoicing and compliance
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-lg border bg-card">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Key className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">Shopify Access</h3>
                    <p className="text-sm text-muted-foreground">
                      Connected automatically during Shopify app install
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-lg border bg-card">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Package className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">Enable Widget</h3>
                    <p className="text-sm text-muted-foreground">
                      Add the storefront widget so customers can split payments
                    </p>
                  </div>
                </div>
              </div>
              <Button 
                onClick={() => handleStepChange(isShopifyQuickOnboarding ? "quick" : "business")}
                className="w-full"
                size="lg"
              >
                {isShopifyQuickOnboarding ? "Continue with Quick Setup" : "Get Started"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}

        {step === "quick" && isShopifyQuickOnboarding && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Rocket className="h-5 w-5" />
                Quick Shopify Setup
              </CardTitle>
              <CardDescription>
                Your store connection and basic store details were imported from Shopify.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="rounded-lg border border-[#95BF47]/30 bg-[#95BF47]/10 p-4">
                <div className="flex items-start gap-3">
                  <Store className="mt-0.5 h-5 w-5 shrink-0 text-[#5f8f2d]" />
                  <div className="min-w-0 space-y-1">
                    <p className="font-medium">{merchant.store_name}</p>
                    <p className="truncate text-sm text-muted-foreground">{merchant.shopify_store_domain}</p>
                    <p className="text-xs text-[#5f8f2d]">Shopify access is already connected.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 text-sm text-muted-foreground">
                <p>Your dashboard will be ready after you accept the terms. You can complete business and payout details later from Settings.</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                    <span>Store name, domain, and currency come from Shopify</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                    <span>Business profile, GST, and PAN can be added later when needed</span>
                  </li>
                </ul>
              </div>

              <div className="rounded-lg border bg-muted/50 p-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="quick-terms"
                    checked={termsAccepted}
                    onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                  />
                  <Label htmlFor="quick-terms" className="cursor-pointer text-sm leading-relaxed">
                    I agree to Shakatah&apos;s{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Terms of Service</a>{" "}
                    and{" "}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Privacy Policy</a>.
                  </Label>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => handleStepChange("welcome")}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button
                  onClick={handleSaveBusinessDetails}
                  disabled={savingBusiness || !termsAccepted}
                  className="flex-1"
                >
                  {savingBusiness ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Continue to Widget
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === "business" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Business Information
              </CardTitle>
              <CardDescription>
                This information is used for invoicing, compliance, and notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  Contact Details
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="business_email">Business Email *</Label>
                    <Input
                      id="business_email"
                      type="email"
                      value={businessDetails.business_email}
                      onChange={(e) => setBusinessDetails(prev => ({ ...prev, business_email: e.target.value }))}
                      placeholder="billing@yourstore.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="business_phone">Business Phone *</Label>
                    <Input
                      id="business_phone"
                      type="tel"
                      value={businessDetails.business_phone}
                      onChange={(e) => setBusinessDetails(prev => ({ ...prev, business_phone: e.target.value }))}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business_address">Business Address</Label>
                  <Textarea
                    id="business_address"
                    value={businessDetails.business_address}
                    onChange={(e) => setBusinessDetails(prev => ({ ...prev, business_address: e.target.value }))}
                    placeholder="Full business address for invoicing"
                    rows={2}
                  />
                </div>
              </div>

              {/* Tax Information */}
              {GST_MODULE_ENABLED ? (
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  Tax Information (Optional)
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="gst_number">GST Number</Label>
                    <Input
                      id="gst_number"
                      value={businessDetails.gst_number}
                      onChange={(e) => setBusinessDetails(prev => ({ ...prev, gst_number: e.target.value.toUpperCase() }))}
                      placeholder="22AAAAA0000A1Z5"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pan_number">PAN Number</Label>
                    <Input
                      id="pan_number"
                      value={businessDetails.pan_number}
                      onChange={(e) => setBusinessDetails(prev => ({ ...prev, pan_number: e.target.value.toUpperCase() }))}
                      placeholder="ABCDE1234F"
                    />
                  </div>
                </div>
              </div>
              ) : null}

              {/* Contact Person */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  Contact Person
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="contact_person_name">Contact Name *</Label>
                    <Input
                      id="contact_person_name"
                      value={businessDetails.contact_person_name}
                      onChange={(e) => setBusinessDetails(prev => ({ ...prev, contact_person_name: e.target.value }))}
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_person_email">Contact Email *</Label>
                    <Input
                      id="contact_person_email"
                      type="email"
                      value={businessDetails.contact_person_email}
                      onChange={(e) => setBusinessDetails(prev => ({ ...prev, contact_person_email: e.target.value }))}
                      placeholder="john@yourstore.com"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_person_phone">Contact Phone (Optional)</Label>
                  <Input
                    id="contact_person_phone"
                    type="tel"
                    value={businessDetails.contact_person_phone}
                    onChange={(e) => setBusinessDetails(prev => ({ ...prev, contact_person_phone: e.target.value }))}
                    placeholder="+91 98765 43210"
                  />
                </div>
              </div>

              {/* Store Category */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  What Are You Selling?
                </h3>
                <div className="space-y-2">
                  <Label htmlFor="store_category">Store Category *</Label>
                  <Select
                    value={businessDetails.store_category}
                    onValueChange={(v) => setBusinessDetails(prev => ({ ...prev, store_category: v, store_category_other: v !== "other" ? "" : prev.store_category_other }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select your store category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fashion_apparel">Fashion & Apparel</SelectItem>
                      <SelectItem value="health_wellness">Health & Wellness</SelectItem>
                      <SelectItem value="beauty_cosmetics">Beauty & Cosmetics</SelectItem>
                      <SelectItem value="electronics">Electronics</SelectItem>
                      <SelectItem value="food_beverages">Food & Beverages</SelectItem>
                      <SelectItem value="home_decor">Home Decor</SelectItem>
                      <SelectItem value="pet_supplies">Pet Supplies</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {businessDetails.store_category === "other" && (
                  <div className="space-y-2">
                    <Label htmlFor="store_category_other">Please specify *</Label>
                    <Input
                      id="store_category_other"
                      value={businessDetails.store_category_other}
                      onChange={(e) => setBusinessDetails(prev => ({ ...prev, store_category_other: e.target.value }))}
                      placeholder="e.g., Sports Equipment, Jewelry, etc."
                    />
                  </div>
                )}
              </div>

              {/* Currency Selection */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  Currency Settings
                </h3>
                <div className="space-y-2">
                  <Label htmlFor="currency">Store Currency *</Label>
                  <Select
                    value={businessDetails.currency}
                    onValueChange={(v) => setBusinessDetails(prev => ({ ...prev, currency: v as Currency }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select your currency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">🇺🇸 USD - US Dollar ($)</SelectItem>
                      <SelectItem value="INR">🇮🇳 INR - Indian Rupee (₹)</SelectItem>
                      <SelectItem value="EUR">🇪🇺 EUR - Euro (€)</SelectItem>
                      <SelectItem value="GBP">🇬🇧 GBP - British Pound (£)</SelectItem>
                      <SelectItem value="AUD">🇦🇺 AUD - Australian Dollar (A$)</SelectItem>
                      <SelectItem value="CAD">🇨🇦 CAD - Canadian Dollar (C$)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    This currency will be used for all financial displays in your dashboard.
                  </p>
                </div>
              </div>

              {/* Terms & Conditions */}
              <div className="p-4 rounded-lg border bg-muted/50 space-y-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms"
                    checked={termsAccepted}
                    onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                  />
                  <Label htmlFor="terms" className="text-sm leading-relaxed cursor-pointer">
                    I agree to Shakatah's{" "}
                    <a href="/terms" target="_blank" className="text-primary hover:underline">
                      Terms of Service
                    </a>{" "}
                    and{" "}
                    <a href="/privacy" target="_blank" className="text-primary hover:underline">
                      Privacy Policy
                    </a>
                    . I understand that Shakatah will process payments on behalf of my customers and that I am responsible for product fulfillment.
                  </Label>
                </div>
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => handleStepChange("welcome")}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button 
                  onClick={handleSaveBusinessDetails}
                  disabled={savingBusiness || !termsAccepted || !businessDetails.business_email || !businessDetails.business_phone || !businessDetails.contact_person_name || !businessDetails.contact_person_email || !businessDetails.store_category || (businessDetails.store_category === "other" && !businessDetails.store_category_other)}
                  className="flex-1"
                >
                  {savingBusiness ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === "permissions" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                How This App Connects to Your Store
              </CardTitle>
              <CardDescription>
                Understanding what access you're granting
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
                <p>
                  When you install this app, Shopify will ask you to approve access to certain parts of your store.
                  This secure connection lets the app work properly and deliver the features you expect.
                </p>
                
                <div className="space-y-3">
                  <h3 className="font-medium text-foreground">What this means for you:</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>You stay fully in control — access is only granted after your approval</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>The app can only access what it needs to function</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>We cannot see your Shopify login details or password</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>You can remove the app (and its access) at any time from your Shopify admin</span>
                    </li>
                  </ul>
                </div>

                <div className="p-4 rounded-lg border bg-muted/50 space-y-3">
                  <h3 className="font-medium text-foreground flex items-center gap-2">
                    <Key className="h-4 w-4 text-primary" />
                    What access you're granting:
                  </h3>
                  <p>
                    The app may read or update specific store data such as products, orders, or settings — only as required for the app's features.
                    We never access anything beyond the permissions you approve.
                  </p>
                  <p className="text-xs">
                    Shopify handles this connection securely using its built-in authorization system.
                  </p>
                </div>
              </div>

              {/* Terms Agreement */}
              <div className="p-4 rounded-lg border bg-card space-y-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="permissions-agreement"
                    checked={permissionsAccepted}
                    onCheckedChange={(checked) => setPermissionsAccepted(checked === true)}
                  />
                  <Label htmlFor="permissions-agreement" className="text-sm leading-relaxed cursor-pointer">
                    I understand and agree to the access permissions described above
                  </Label>
                </div>
                <a 
                  href="/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  <FileText className="h-4 w-4" />
                  View complete Terms and Conditions
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => handleStepChange("business")}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button 
                  onClick={() => handleStepChange("widget")}
                  disabled={!permissionsAccepted}
                  className="flex-1"
                >
                  Continue to Widget Setup
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === "widget" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {widgetInstalled ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <Code className="h-5 w-5" />
                )}
                {widgetInstalled ? "Widget Installed" : "Install the Widget"}
              </CardTitle>
              <CardDescription>
                {widgetInstalled 
                  ? "The split payment option is now available on your cart page"
                  : "Enable split payments for your customers with one click"
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {isWooCommerceMerchant ? (
                <Alert className="border-primary/30 bg-primary/5">
                  <CheckCircle className="h-4 w-4 text-primary" />
                  <AlertDescription className="text-muted-foreground">
                    Your WooCommerce account is ready. Install the connector plugin in WordPress, then paste the details below in <strong>WooCommerce → Splitkart</strong>.
                  </AlertDescription>
                </Alert>
              ) : storeConnected ? (
                <Alert className="border-primary/30 bg-primary/5">
                  <CheckCircle className="h-4 w-4 text-primary" />
                  <AlertDescription className="text-muted-foreground">
                    Your Shopify store is already connected through the app install flow. The only thing left is enabling the storefront widget.
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert className="border-amber-500/50 bg-amber-500/10">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-700">
                    We are still waiting for Shopify store access to finish syncing. Refresh this page or reopen the app from Shopify admin before installing the widget.
                  </AlertDescription>
                </Alert>
              )}

              {!widgetInstalled ? (
                <>
                  {isWooCommerceMerchant ? (
                    <div className="space-y-4">
                      <Label className="text-sm font-medium">WooCommerce Connector Settings</Label>
                      <div className="grid gap-3">
                        <div className="rounded-lg border bg-muted/30 p-4">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Splitkart App URL</p>
                          <code className="mt-2 block break-all rounded bg-background px-3 py-2 text-sm">https://splitkart.shakatah.com</code>
                        </div>
                        <div className="rounded-lg border bg-muted/30 p-4">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Merchant Token</p>
                          <code className="mt-2 block break-all rounded bg-background px-3 py-2 text-sm">{merchant.id}</code>
                        </div>
                        <div className="rounded-lg border bg-muted/30 p-4">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Webhook Secret</p>
                          <code className="mt-2 block break-all rounded bg-background px-3 py-2 text-sm">{merchant.webhook_secret || "Refresh this page to generate your webhook secret"}</code>
                        </div>
                        <div className="rounded-lg border bg-muted/30 p-4">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Store URL</p>
                          <code className="mt-2 block break-all rounded bg-background px-3 py-2 text-sm">{merchant.shopify_store_domain}</code>
                        </div>
                      </div>
                      <Alert className="border-blue-500/30 bg-blue-500/5">
                        <Info className="h-4 w-4 text-blue-600" />
                        <AlertDescription className="text-sm text-muted-foreground">
                          After saving these values in WordPress, the Split with Friends button will appear on WooCommerce product and cart pages.
                        </AlertDescription>
                      </Alert>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Label className="text-sm font-medium">What We'll Add to Your Store</Label>
                      <div className="grid gap-3">
                        <div className="flex items-start gap-3 p-4 rounded-lg border bg-primary/5 border-primary/20">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Package className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">Cart Page Split Button</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              A "Split your payment with friends" button appears on your cart page, allowing customers to share the cost with friends & family before checkout.
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-4 rounded-lg border bg-purple-500/5 border-purple-500/20">
                          <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                            <Store className="h-5 w-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">Homepage Promotional Banner</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              A subtle banner promotes the split payment feature on your homepage. You can customize the position or disable it from your dashboard.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* How it works */}
                  <div className="space-y-4">
                    <Label className="text-sm font-medium">How Split Payments Work</Label>
                    <div className="grid gap-3">
                      <div className="flex items-start gap-3 p-3 rounded-lg border bg-muted/30">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-primary font-bold text-sm">1</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Customer adds products to cart</p>
                          <p className="text-xs text-muted-foreground">Works with any products in their cart—no restrictions</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 rounded-lg border bg-muted/30">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-primary font-bold text-sm">2</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Customer clicks "Split with Friends"</p>
                          <p className="text-xs text-muted-foreground">They invite friends via email and set how much each person pays</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 rounded-lg border bg-muted/30">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-primary font-bold text-sm">3</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Friends receive payment links</p>
                          <p className="text-xs text-muted-foreground">Each friend gets a personalized link to pay their share securely</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 rounded-lg border bg-muted/30">
                        <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-green-600 font-bold text-sm">4</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Order auto-submits when fully paid</p>
                          <p className="text-xs text-muted-foreground">Once everyone pays, the order is placed automatically in your store</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Info note */}
                  <Alert className="border-blue-500/30 bg-blue-500/5">
                    <Info className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-sm text-muted-foreground">
                      <strong className="text-foreground">Store-wide by default:</strong> Split payments work across your entire store. You can manage settings and view analytics from your dashboard after setup.
                    </AlertDescription>
                  </Alert>

                  {/* One-Click Install Button */}
                  <Button 
                    onClick={handleInstallWidget}
                    disabled={installingWidget || !storeConnected}
                    size="lg"
                    className="w-full"
                  >
                    {installingWidget ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Rocket className="h-4 w-4 mr-2" />
                    )}
                    {isWooCommerceMerchant ? "I've Installed the WooCommerce Plugin" : "Install Widget Automatically"}
                  </Button>

                </>
              ) : (
                <div className="space-y-4">
                  <Alert className="border-green-500/50 bg-green-500/10">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-600">
                      <p className="font-medium">Widget installed successfully!</p>
                      <p className="text-sm mt-1">
                        The split payment button is now active on your cart page. A promotional banner will also appear on your homepage.
                      </p>
                    </AlertDescription>
                  </Alert>
                  
                  <div className="grid gap-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>Cart page: "Split your payment with friends" button</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>Homepage: Promotional banner (customizable in dashboard)</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => handleStepChange("permissions")}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button 
                  onClick={() => handleStepChange("complete")}
                  className="flex-1"
                >
                  {widgetInstalled ? "Complete Setup" : "Skip for Now"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === "complete" && (
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl">You're All Set!</CardTitle>
              <CardDescription className="text-base">
                Your 30-day intro trial has started with 100 free splits and 0% commission.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-3">
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-green-500/5 border-green-500/20">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span>Business details saved</span>
                </div>
                <div className={`flex items-center gap-3 p-3 rounded-lg border ${storeConnected ? 'bg-green-500/5 border-green-500/20' : 'bg-amber-500/5 border-amber-500/20'}`}>
                  {storeConnected ? (
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  )}
                  <span>
                    {isWooCommerceMerchant
                      ? "WooCommerce store registered"
                      : storeConnected
                        ? "Shopify API connected automatically"
                        : "Shopify access still syncing"}
                  </span>
                </div>
                <div className={`flex items-center gap-3 p-3 rounded-lg border ${widgetInstalled ? 'bg-green-500/5 border-green-500/20' : 'bg-amber-500/5 border-amber-500/20'}`}>
                  {widgetInstalled ? (
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  )}
                  <span>
                    {widgetInstalled
                      ? 'Split payment widget active on cart page'
                      : requiresThemeExtension
                        ? 'Theme app embed still needs activation in Shopify Theme Editor'
                        : 'Widget installation skipped'}
                  </span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-primary/5 border-primary/20">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  <span>30-day trial activated (100 free splits and 0% commission)</span>
                </div>
              </div>

              {!widgetInstalled && !requiresThemeExtension && (
                <Alert className="border-amber-500/50 bg-amber-500/10">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-700">
                    <p className="font-medium">Widget not yet installed</p>
                    <p className="text-sm mt-1">
                      You can install the widget later from <strong>Dashboard → Settings → Widget</strong>.
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              {requiresThemeExtension && !widgetInstalled && (
                <Alert className="border-blue-500/50 bg-blue-500/10">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-700">
                    <p className="font-medium">Theme app embed required</p>
                    <p className="text-sm mt-1">
                      Open Shopify Theme Editor and enable the <strong>Splitkart</strong> app embed to turn on the storefront widget.
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              {widgetInstalled && (
                <Alert className="border-green-500/50 bg-green-500/10">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-700">
                    <p className="font-medium">Widget is live!</p>
                    <p className="text-sm mt-1">
                      The split payment button is now showing on your cart page, and a banner appears on your homepage.
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              <Button 
                onClick={handleComplete}
                className="w-full"
                size="lg"
              >
                Go to Dashboard
                <Rocket className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MerchantOnboarding;
